import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, Phone, Mail, Download } from "lucide-react";
import OnboardingLogo from "../components/onboarding/OnboardingLogo";
import { BACKEND_URL } from "../lib/backendUrl";

const BACKEND = BACKEND_URL;
const SUPPORT_PHONE = "+91 95456 40658";
const SUPPORT_EMAIL = "notification@c-ore.in";

const page = { minHeight: "100vh", background: "#FFFFFF", fontFamily: "-apple-system,Segoe UI,Roboto,sans-serif" };
const wrap = { maxWidth: 720, margin: "0 auto", padding: "48px 24px" };
const h1   = { fontSize: 24, fontWeight: 700, color: "#1E3A5F", margin: "32px 0 8px 0" };
const lead = { fontSize: 16, color: "#6B7280", lineHeight: 1.5 };
const hr   = { border: "none", borderTop: "1px solid #E5E7EB", margin: "32px 0" };

const stepCard = {
  background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 12,
  padding: 24, marginBottom: 16, display: "flex", gap: 20,
};
const circle = (color) => ({
  width: 40, height: 40, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
  borderRadius: "50%", background: color, color: "#FFFFFF", fontWeight: 700, fontSize: 16,
});
const stepTitle = { color: "#1E3A5F", fontWeight: 700, fontSize: 16, marginBottom: 8 };
const body14 = { color: "#374151", fontSize: 14, lineHeight: 1.6, margin: 0 };
const muted = { color: "#6B7280", fontSize: 13, lineHeight: 1.6, marginTop: 8 };

const codeBox = {
  background: "#0F1B2D", color: "#A7F3D0", fontFamily: "Menlo,Consolas,monospace",
  fontSize: 13, padding: "12px 16px", borderRadius: 8, marginTop: 10, lineHeight: 1.8,
  whiteSpace: "pre",
};

const faqCard = {
  background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 8,
  padding: "16px 20px", marginBottom: 8, cursor: "pointer",
};

const supportBox = {
  background: "#EFF6FF", borderRadius: 12, padding: 28, marginTop: 32, textAlign: "center",
};

const FAQS = [
  {
    q: "Is this safe to install?",
    a: "Yes. The C-ORE Tally agent only reads your financial data — it cannot modify or delete anything in Tally. All data is transmitted securely using HTTPS encryption and stored on Indian servers. The agent is a simple Python script — you can view its code directly.",
  },
  {
    q: "Does it work when Tally is closed?",
    a: "The agent requires Tally to be open and running to sync data. We recommend keeping Tally minimised in the Windows taskbar during business hours. If Tally is closed the agent waits and syncs automatically as soon as Tally is opened again. No data is ever lost.",
  },
  {
    q: "How do I stop or uninstall it?",
    a: "Close the agent window by pressing Ctrl+C or simply close the Command Prompt window. To uninstall, permanently delete the agent folder from your computer. Uninstalling does not affect your Tally data or your C-ORE dashboard history.",
  },
  {
    q: "What exactly does it sync?",
    a: "The agent syncs your cash and bank balances, monthly sales and purchase totals, outstanding amounts from customers (debtors) and suppliers (creditors), inventory stock values, and GST summary figures. It does not sync individual employee records, personal information, or any data outside your financial ledgers.",
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`faq-${q.slice(0, 12)}`} style={faqCard} onClick={() => setOpen((v) => !v)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: "#1E3A5F", fontWeight: 700, fontSize: 14 }}>{q}</span>
        <ChevronDown size={16} style={{ color: "#1E3A5F", transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />
      </div>
      {open && (
        <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.6, marginTop: 10, marginBottom: 0 }}>
          {a}
        </p>
      )}
    </div>
  );
}

export default function TallyConnectPage() {
  const search = new URLSearchParams(useLocation().search);
  const token = (search.get("token") || "").trim();
  const downloadUrl = token ? `${BACKEND}/api/tally/download/${token}` : "";

  return (
    <div data-testid="connect-tally-page" style={page}>
      <div style={wrap}>
        <div style={{ marginBottom: 32 }}>
          <OnboardingLogo />
        </div>

        <h1 style={h1}>Connect Your Tally to C-ORE</h1>
        <p style={lead}>
          Sync your Tally data automatically every hour — no manual work needed.
        </p>

        <hr style={hr} />

        {/* Step 1 */}
        <div data-testid="step-1" style={stepCard}>
          <div style={circle("#059669")}>1</div>
          <div style={{ flex: 1 }}>
            <div style={stepTitle}>Download Your Agent Files</div>
            {token ? (
              <>
                <a
                  data-testid="download-config-btn"
                  href={downloadUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    background: "#059669", color: "white", borderRadius: 8,
                    padding: "12px 24px", fontSize: 15, fontWeight: 700,
                    textDecoration: "none", marginTop: 4,
                  }}
                >
                  <Download size={16} /> Download Agent (ZIP)
                </a>
                <p style={muted}>
                  A single ZIP file will download with everything you need —
                  your personalised config.ini, the agent, and the installer.
                  Right-click → <em>Extract All</em>, then continue with Step 2.
                </p>
              </>
            ) : (
              <p style={body14}>
                Please use the download link sent to your email by your C-ORE customer success team.
                If you cannot find the email contact us at <strong>{SUPPORT_PHONE}</strong>.
              </p>
            )}
          </div>
        </div>

        {/* Step 2 */}
        <div data-testid="step-2" style={stepCard}>
          <div style={circle("#1E3A5F")}>2</div>
          <div style={{ flex: 1 }}>
            <div style={stepTitle}>Install and Run</div>
            <p style={body14}>Two options to get started.</p>
            <div style={{ color: "#1E3A5F", fontWeight: 700, fontSize: 13, marginTop: 12 }}>
              Option A — Easiest
            </div>
            <p style={body14}>Double-click <code>install_windows.bat</code>. Runs everything automatically.</p>
            <div style={{ color: "#1E3A5F", fontWeight: 700, fontSize: 13, marginTop: 12 }}>
              Option B — Manual
            </div>
            <p style={body14}>Open Command Prompt and run:</p>
            <div data-testid="manual-code" style={codeBox}>{"pip install -r requirements.txt\npython agent.py"}</div>
            <p style={muted}>
              Keep the agent window open or minimise it. It runs silently in the background.
            </p>
          </div>
        </div>

        {/* Step 3 */}
        <div data-testid="step-3" style={stepCard}>
          <div style={circle("#1E3A5F")}>3</div>
          <div style={{ flex: 1 }}>
            <div style={stepTitle}>Your Dashboard Goes Live</div>
            <p style={body14}>
              Your C-ORE dashboard will show your real Tally data within 5 minutes of starting the agent.
              After that it syncs automatically every hour — no action needed from you.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#10B981" }} />
              <span style={{ color: "#059669", fontSize: 13, fontWeight: 600 }}>
                First sync completes within 5 minutes
              </span>
            </div>
          </div>
        </div>

        <h2 style={{ color: "#1E3A5F", fontWeight: 700, fontSize: 18, marginTop: 32, marginBottom: 16 }}>
          Frequently Asked Questions
        </h2>
        {FAQS.map((f) => (<FAQItem key={f.q} q={f.q} a={f.a} />))}

        <div data-testid="support-box" style={supportBox}>
          <h3 style={{ color: "#1E3A5F", fontWeight: 700, fontSize: 16, margin: 0 }}>
            Need help with installation?
          </h3>
          <p style={{ color: "#6B7280", fontSize: 14, margin: "8px 0 20px 0" }}>
            Our customer success team will guide you through the entire setup on a quick call.
            You do not need any technical knowledge.
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 32, flexWrap: "wrap" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#374151", fontSize: 14, fontWeight: 600 }}>
              <Phone size={14} /> {SUPPORT_PHONE}
            </div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#374151", fontSize: 14 }}>
              <Mail size={14} /> {SUPPORT_EMAIL}
            </div>
          </div>
          <p style={{ color: "#9CA3AF", fontSize: 12, marginTop: 12 }}>
            Available Monday to Saturday — 9am to 7pm IST
          </p>
        </div>
      </div>
    </div>
  );
}
